import { FC, useMemo } from 'react';
import Chart from 'highcharts-react-official';
import Highcharts from 'highcharts';
import noData from 'highcharts/modules/no-data-to-display';
import { blue, green, red } from '@carbon/colors';
import { fromDateParam } from 'utils/dates';
import type {
  EntityLibraryAnalyticsResponse,
  EntityLibraryAnalyticsTrendPoint,
} from 'types/api-responses';

noData(Highcharts);

interface AnalyticsTrendChartProps {
  trend: EntityLibraryAnalyticsTrendPoint[];
  // The server-applied window — can differ from the requested one (clamped),
  // so show it.
  window: EntityLibraryAnalyticsResponse['window'];
}

// Adds vs. removals per day (columns) plus the running library size (line,
// secondary axis). Removals plot as negative so the split around zero reads as
// "cleaning below the line, growing above it".
export const AnalyticsTrendChart: FC<AnalyticsTrendChartProps> = ({
  trend,
  window,
}) => {
  const options = useMemo<Highcharts.Options>(() => {
    const categories = trend.map((p) => p.date);
    const added = trend.map((p) => p.entitiesAdded);
    const removed = trend.map((p) => -p.entitiesRemoved);
    const cumulative = trend.map((p) => p.cumulativeEntities);

    return {
      chart: { height: 320, animation: false },
      title: { text: undefined },
      subtitle: {
        text: `${fromDateParam(window.startDate)} – ${fromDateParam(
          window.endDate
        )}`,
        align: 'right',
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories,
        tickInterval: Math.ceil(Math.max(categories.length, 1) / 8),
        labels: { style: { fontSize: '10px' } },
      },
      yAxis: [
        { title: { text: 'Per day' }, allowDecimals: false },
        {
          title: { text: 'Library size' },
          opposite: true,
          allowDecimals: false,
        },
      ],
      legend: { enabled: true },
      plotOptions: {
        column: { borderWidth: 0 },
        series: { animation: false },
      },
      series: [
        {
          type: 'column',
          name: 'Added',
          data: added,
          color: green[50],
          yAxis: 0,
        },
        {
          type: 'column',
          name: 'Removed',
          data: removed,
          color: red[50],
          yAxis: 0,
        },
        {
          // The running size deliberately reflects ALL activity, not the panel
          // filters — say so, or filtered bars against an unresponsive line
          // read as a broken chart.
          type: 'line',
          name: 'Library size (all activity)',
          data: cumulative,
          color: blue[60],
          yAxis: 1,
          lineWidth: 1,
          marker: { enabled: false },
        },
      ],
      lang: { noData: 'No activity in this window' },
    };
  }, [trend, window]);

  return <Chart highcharts={Highcharts} options={options} />;
};
